/**
 * Central de notificações — folha aberta pelo sino.
 *
 * Lê `/trips/:id/notifications`: primeiro o que pede algo de quem olha
 * (despesa aguardando confirmação), depois o diário do grupo, do mais
 * recente para o mais antigo, incluindo as próprias ações. Tocar num item
 * abre a coisa. Vazio é "Tudo em dia" — não é erro.
 *
 * A folha é a mesma das outras (`.sheet`): fecha tocando o fundo.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Icon } from '../../ui/icons';
import { Sheet } from '../../ui/Sheet';
import { EmptyState, ErrorState, Skeleton } from '../../ui/primitives';
import { fraseDe, iconeDe, type Notificacao, quando } from './notificacoes';

async function ler(tripId: string): Promise<{ notifications: Notificacao[]; awaitingMe: number }> {
  const res = await fetch(`/api/v1/trips/${tripId}/notifications`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar (${res.status})`);
  return (await res.json()) as { notifications: Notificacao[]; awaitingMe: number };
}

export function useNotificacoes(tripId: string) {
  return useQuery({
    queryKey: ['notificacoes', tripId],
    queryFn: () => ler(tripId),
    // O sino é visto a cada abertura da Home; um minuto de cache evita
    // bater no servidor a cada troca de aba sem deixar a lista velha.
    staleTime: 60_000,
  });
}

export function NotificacoesSheet({ tripId, onClose }: { tripId: string; onClose: () => void }) {
  const navegar = useNavigate();
  const lista = useNotificacoes(tripId);
  const agora = Date.now();

  function abrir(n: Notificacao) {
    onClose();
    if (n.route) navegar(n.route);
  }

  return (
    <Sheet titleId="notif-titulo" onFechar={onClose} className="notif">
        <h2 className="sheet__title" id="notif-titulo">
          Notificações
        </h2>

        {lista.isLoading && <Skeleton height={56} count={3} />}

        {lista.isError && (
          <ErrorState
            title="Não foi possível carregar"
            description="Verifique a conexão e tente de novo."
            onRetry={() => void lista.refetch()}
          />
        )}

        {lista.isSuccess && lista.data.notifications.length === 0 && (
          <EmptyState
            ilustracao="concluido"
            title="Tudo em dia"
            description="Quando alguém do grupo mexer no roteiro ou registrar uma despesa, aparece aqui."
          />
        )}

        {lista.isSuccess && lista.data.notifications.length > 0 && (
          <ul className="notif__lista">
            {lista.data.notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={n.kind === 'expense_awaiting_me' ? 'notif__item notif__item--pede' : 'notif__item'}
                  onClick={() => abrir(n)}
                  disabled={!n.route}
                >
                  <span className="notif__icone" aria-hidden="true">
                    <Icon name={iconeDe(n.kind)} size={18} />
                  </span>
                  <span className="notif__texto">
                    <span className="notif__frase">{fraseDe(n)}</span>
                    <span className="notif__quando">{quando(Date.parse(n.at), agora)}</span>
                  </span>
                  {n.route && (
                    <span className="notif__seta" aria-hidden="true">
                      <Icon name="avancar" size={16} />
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
    </Sheet>
  );
}
